import 'dayjs/locale/de';
import 'dayjs/locale/en';
import './i18n';
import './theme/fonts.css';

import { setLanguage } from '@store/slices/configSlice';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  LANGUAGE,
  LANGUAGE_TYPE,
  LOCAL_STORAGE_TYPE,
  mappableCompanyRouteKeys,
} from '@utils/constants';
import ErrorBoundary, { FallBackUI } from '@utils/ErrorBoundary';
import { useCallback, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useDispatch } from 'react-redux';

import Providers from '@context/Providers';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import GlobalStyle from '@theme/GlobalStyle';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import Routes from './routes';
import ReduxProvider from './store/ReduxProvider';
import { initGA } from '@utils/analytics';
import { GA_MEASUREMENT_ID } from '@utils/apiHooks';

const queryClient = new QueryClient();

function App() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const valueLanguage = window.localStorage.getItem(
    LOCAL_STORAGE_TYPE.LANGUAGE
  );

  // Initialize Google Analytics
  useEffect(() => {
    const measurementId = GA_MEASUREMENT_ID;
    if (measurementId) {
      initGA(measurementId);
    } else {
      console.warn('GA_MEASUREMENT_ID is not set');
    }
  }, []);

  const updateLanguage = useCallback(
    (language: (typeof LANGUAGE_TYPE)[number]) => {
      void i18n.changeLanguage(language);
      dispatch(setLanguage(language));
      dayjs.locale(language);
    },
    [dispatch, i18n]
  );

  useEffect(() => {
    // Dynamically map translated paths to languages
    const pathToLangMap: Record<string, any> = {};

    for (const lang of [LANGUAGE.ENGLISH, LANGUAGE.GERMAN]) {
      for (const key of mappableCompanyRouteKeys) {
        const translatedPath = i18n.t(`mappable-company-routes.${key}`, {
          lng: lang,
        });
        pathToLangMap[translatedPath] = lang;
      }
    }

    const matchedLangEntry = Object.entries(pathToLangMap).find(([path]) =>
      window.location.pathname.startsWith(path)
    );
    const pathLang = matchedLangEntry?.[1];

    const preferredLang = pathLang || valueLanguage;

    const isValid = [LANGUAGE.ENGLISH, LANGUAGE.GERMAN].includes(preferredLang);
    const finalLang = isValid ? preferredLang : LANGUAGE.ENGLISH;

    updateLanguage(finalLang);
    if (!valueLanguage || valueLanguage !== finalLang) {
      window.localStorage.setItem(LOCAL_STORAGE_TYPE.LANGUAGE, finalLang);
    }
  }, []);

  return (
    <ErrorBoundary fallBackUIComponent={FallBackUI()}>
      <GlobalStyle />
      <Routes />
    </ErrorBoundary>
  );
}

export default function WithProviders() {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <HelmetProvider>
        <ReduxProvider>
          <QueryClientProvider client={queryClient}>
            <Providers>
              <App />
            </Providers>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ReduxProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}
